// eslint-disable-next-line import/no-cycle
import type { MangoQuery, MangoQuerySelector } from 'rxdb';

import {
type ClientState,
  type SettingsState,
  AnyDocument,
  BaseDocument, DocumentLocation,
  DocumentWithTransientData,
  FeedDocumentLocation,
  FilteredView,
  FullZustandState,
  Highlight,
  Note,
  PartialDocument,
  RssFeed,
  RssFolderOrItem,
  SortKey,
  SortRule,
} from '../types';
import { isExtension, isMobile, isWebApp } from '../utils/environment';
import makeLogger from '../utils/makeLogger';
import createInitialTransientDocumentData from './createInitialTransientDocumentData';
// eslint-disable-next-line import/no-cycle
import database from './database';
// eslint-disable-next-line import/no-cycle
import { DEFAULT_DOCUMENT_SORT_RULES, globalState, isStaffProfile } from './models';
import { areBookwiseWipFeaturesEnabled } from './useAreBookwiseWipFeaturesEnabled';
import fixBadDocumentLocationsValue from './utils/fixBadDocumentLocationsValue';
import getSortActiveViewName from './utils/getSortActiveViewName';

const logger = makeLogger(__filename);

export const getCurrentSortRule = ({
  filterByFeedDocumentLocation,
  filterByDocumentLocation,
  listId,
  sortRules,
  sortRulesKey,
  state = globalState.getState(),
  isDigestView,
}: {
  filterByFeedDocumentLocation?: FeedDocumentLocation;
  filterByDocumentLocation?: DocumentLocation;
  listId?: string;
  sortRules?: SortRule[];
  sortRulesKey?: string;
  state?: FullZustandState;
  isDigestView?: boolean;
}): SortRule => {
  const rulesMap = DEFAULT_DOCUMENT_SORT_RULES;
  const defaultRules = rulesMap[DocumentLocation.New];

  if (sortRules?.length) {
    return sortRules[0];
  }

  let rules: SortRule[];
  if (listId && state.client.listSortRules && state.client.listSortRules[listId]) {
    rules = state.client.listSortRules[listId];
  } else {
    const sortKey = getSortActiveViewName({
      activeDocumentLocation: filterByFeedDocumentLocation ?? filterByDocumentLocation,
      documentLocations: fixBadDocumentLocationsValue(state.persistent.settings.documentLocations),
      isFeedView: Boolean(filterByFeedDocumentLocation),
    });
    rules = Array.from(rulesMap[(sortRulesKey ?? sortKey) as keyof typeof rulesMap] as ReadonlyArray<SortRule>);
  }
  const currentRules = rules ?? defaultRules;
  const currentRule = currentRules[0];

  if (isDigestView) {
    // In digest view if you are sorting by LastStatusUpdate (last moved) and move the document
    // to a different location, it causes the item to disappear from the focus because the
    // list gets reordered. To avoid this we change the sort rule to SavedAt.
    if (currentRule.key === SortKey.LastStatusUpdate) {
      return {
        ...currentRule,
        key: SortKey.SavedAt,
      };
    }
  }

  return currentRule;
};

// Note: if document has no transientData, we attach a new transientData but don't save it to our Zustand state.
export async function getDocumentWithTransientData<T extends AnyDocument>(
  id: BaseDocument['id'] | null | void,
  state: FullZustandState = globalState.getState(),
): Promise<DocumentWithTransientData<T> | null> {
  if (!id) {
    return null;
  }
  const doc = await database.collections.documents.findOne<T>(id);
  if (!doc) {
    return null;
  }
  const transientData = state.transientDocumentsData[id] ?? createInitialTransientDocumentData();
  return {
    ...doc,
    transientData,
  };
}

export async function getDocument<T extends AnyDocument>(id: BaseDocument['id']): Promise<T | null> {
  return database.collections.documents.findOne<T>(id);
}

export async function getDocuments<T extends AnyDocument>(ids: BaseDocument['id'][]): Promise<T[]> {
  return database.collections.documents.findByIds<T>(ids);
}

/**
 * Retrieves documents from the database in batches. Batches and sorts by document ID.
 */
export async function iterateThroughDocumentsInBatches<T extends AnyDocument = AnyDocument>(
  selector: MangoQuerySelector<T>,
  handleBatch: (batch: T[], batchNum: number) => Promise<void>,
  options: {
    batchSize?: number;
    shouldExpensivelyCountDocsLeft?: boolean; // for debug purposes, don't leave on in production
  } = {},
): Promise<void> {
  await database.initialize();
  // We're trying to strike a balance between:
  //    1. total number of queries fired, which increases the time that this function runs, and
  //    2. the cost of a single query i.e. how much it freezes up the app.
  const batchSize = options.batchSize ?? (isMobile ? 50 : 250);

  let batchNum = 0;
  let lastId = '';
  while (true) {
    const timerId = Math.random().toString().slice(2);
    const batchQuery: MangoQuery<AnyDocument> = {
      selector: {
        ...selector,
        id: {
          $gt: lastId,
        },
      },
      limit: batchSize,
      sort: [
        {
          id: 'asc',
        },
      ],
    };
    logger.time(`iterateThroughDocumentsInBatches batch ${batchNum} #${timerId}`);
    logger.debug(`iterateThroughDocumentsInBatches batch ${batchNum} #${timerId} preparing`, {
      batchQuery,
      batchSize,
    });
    const documentBatch = await database.collections.documents.find<T>(batchQuery);
    if (options.shouldExpensivelyCountDocsLeft) {
      const queryForCount: MangoQuery<AnyDocument> = {
        ...batchQuery,
        limit: undefined,
      };
      const docsLeft = await database.collections.documents.count(queryForCount);
      logger.debug(
        `iterateThroughDocumentsInBatches batch ${batchNum} #${timerId} docs left: ${docsLeft}`,
      );
    }
    logger.debug(`iterateThroughDocumentsInBatches batch ${batchNum} #${timerId} results`, {
      documentBatch,
      batchQuery,
      batchSize,
    });
    logger.timeEnd(`iterateThroughDocumentsInBatches batch ${batchNum} #${timerId}`);
    if (documentBatch.length === 0) {
      break;
    }
    await handleBatch(documentBatch, batchNum);
    batchNum += 1;
    const lastDoc = documentBatch[documentBatch.length - 1];
    lastId = lastDoc.id;
    logger.debug(`iterateThroughDocumentsInBatches batch ${batchNum} #${timerId} next params`, {
      lastDoc,
      lastId,
    });
  }
}

export const getFeed = <T = RssFeed>(
  id: string | null | void,
  state: FullZustandState = globalState.getState(),
): T | null => {
  if (!id || !state.persistent.rssFeeds) {
    return null;
  }
  return state.persistent.rssFeeds[id] as unknown as T;
};

export const getView = <T = FilteredView>(
  id: string | null | void,
  state: FullZustandState = globalState.getState(),
): T | null => {
  if (!id || !state.persistent.filteredViews) {
    return null;
  }
  return state.persistent.filteredViews[id] as unknown as T;
};

export const getRssFolderOrItem = <T = RssFolderOrItem>(
  id: string | null | void,
  state: FullZustandState = globalState.getState(),
): T | null => {
  if (!id || !state.persistent.rssFoldersAndItems) {
    return null;
  }

  return state.persistent.rssFoldersAndItems.find((item) => item.id === id) as unknown as T;
};

export const getIsAutoHighlightingEnabled = (
  state: FullZustandState = globalState.getState(),
): boolean => {
  if (isExtension) {
    // Defaults to false
    return Boolean(state.persistent.settings.extension?.isAutoHighlightingEnabled);
  }
  // We do `!== false` because it may not exist. In that case we default to true
  if (isMobile) {
    return state.persistent.settings.mobile?.autoHighlight !== false;
  }
  if (isWebApp) {
    return state.persistent.settings.web?.isAutoHighlightingEnabled !== false;
  }
  throw new Error('Unknown platform');
};

export const getAskToPasteUrl = (state: FullZustandState = globalState.getState()): boolean => {
  return state.client.askToPasteUrls;
};

export const getAutoKindleEmailDelivery = (
  state: FullZustandState = globalState.getState(),
): boolean => {
  return Boolean(state.persistent.integrations.kindle?.isAutomaticKindleEmailEnabled);
};

export const getKindleExportFrequency = (
  state: FullZustandState = globalState.getState(),
): 'weekly' | 'daily' => {
  return state.persistent.integrations.kindle?.exportFrequency ?? 'weekly';
};

// NOTE: Use the useHighlightNote() DB hook if you can. This function is only for one-time retrieval.
export async function getNoteFromHighlight(
  highlight: Highlight | PartialDocument<Highlight, 'id' | 'children'> | void | null,
): Promise<NonNullable<Note['content']>> {
  if (!highlight || !highlight.children || highlight.children.length === 0) {
    return '';
  }

  if (highlight.children.length > 1) {
    logger.warn(
      `Highlight#${highlight.id} has more than one child. This should not happen, highlights can only have one (note) child. This function will only return the first one.`,
      { highlight },
    );
  }
  const note = await database.collections.documents.findOne(highlight.children[0]);
  return note?.content ?? '';
}

export const getFeatureFlag = <T extends keyof ClientState['featureFlags']>(
  settingName: T,
  state: FullZustandState = globalState.getState(),
) => {
  const settingValue = state.client.featureFlags[settingName];
  const isStaff = isStaffProfile(state);
  return isStaff && settingValue;
};

export function getIsGhostreaderEnabled(state: FullZustandState): boolean {
  const ghostreaderSetting = state.persistent.settings.isGhostreaderEnabled;
  return ghostreaderSetting !== false && areBookwiseWipFeaturesEnabled(state);
}

export function getMobileToolbarId<T extends 'mobileLongFormToolbar' | 'mobileShortFormToolbar'>(
  state: FullZustandState,
  key: T,
): NonNullable<SettingsState[T]>['id'] {
  if (state.persistent.settings[key]) {
    return state.persistent.settings[key]!.id;
  }
  if (getIsGhostreaderEnabled(state)) {
    return 'classic';
  }
  return 'modern';
}
